/**
 * Shared scorecard step — used by both InterviewActivity (plan editor)
 * and ScheduleInterview (scheduling wizard).
 *
 * Orchestrates three data sources into the `ScorecardTemplateBuilder`:
 *   - Create + plan     → seed from `interviewPlan` via
 *                         `resolvePlanScorecardSource`
 *   - Edit              → hydrate from `payloadRef.current.scorecard`
 *   - Adhoc / no plan   → auto-select the first ATS template as a
 *                         starter so the editor isn't empty
 *
 * Pure mapping helpers live in `valueMappers.ts`; plan shape sniffing
 * lives in `planScorecardResolver.ts`. The header (enable toggle +
 * AI toggle + template picker) is its own file.
 */
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Flex } from '@chakra-ui/react';
import {
  ScorecardTemplateBuilder,
  type ScorecardTemplateBuilderHandle,
  type ScorecardTemplateFormValues,
} from '@kula-ai/components';
import { ScorecardAddQuestion } from '@/components/shared/scorecard/ScorecardAddQuestion';
import type { StepFormApi } from '@/hooks/useStepValidation';
import { useScorecardTemplates } from '../../ScheduleInterview/api/useScorecardTemplates';
import { ScorecardStepHeader } from './ScorecardStepHeader';
import type { ScorecardStepProps } from './ScorecardStep.types';
import { templateToFormValues, payloadToFormValues } from './valueMappers';
import { resolvePlanScorecardSource } from './planScorecardResolver';

export const ScorecardStep = memo(
  forwardRef<StepFormApi, ScorecardStepProps>(
    (
      {
        payloadRef,
        prefillVersion = 0,
        copy,
        showAiToggle = true,
        interviewPlan,
      },
      ref
    ) => {
      const { templates, templateOptions } = useScorecardTemplates();
      const formRef = useRef<ScorecardTemplateBuilderHandle>(null);

      const [scorecardEnabled, setScorecardEnabled] = useState(true);
      const [aiEnabled, setAiEnabled] = useState(true);
      const [selectedTemplate, setSelectedTemplate] = useState<string | null>(
        null
      );

      const [initialValues, setInitialValues] = useState<
        Partial<ScorecardTemplateFormValues>
      >(() => {
        const existing = payloadRef.current.scorecard;
        if (existing && 'questions' in existing) {
          return payloadToFormValues(existing);
        }
        return {};
      });

      const didAutoSelectTemplate = useRef(false);
      /** Plan-supplied questions — skip the "first template" fallback below. */
      const scorecardSeededFromInterviewPlan = useRef(false);
      /** Single-fire guard for the plan-seed effect: `interviewPlan` upstream
       *  is not memoized, so the effect's deps array tries to re-run on every
       *  parent render. Re-firing `reset()` mid-edit duplicates field-array
       *  rows (RHF `useFieldArray` desync) and wipes any in-progress user edit.
       *  Seed exactly once per mount. */
      const didPlanSeed = useRef(false);
      /** `prefillVersion` the lazy `useState` init above already consumed on
       *  the current mount. The edit-hydrate effect skips its `reset()` for
       *  that same version (the form already has the data via `defaultValues`,
       *  re-asserting it triggers a `useFieldArray` double-init that ghosts
       *  rows). Subsequent bumps still re-hydrate. */
      const initialPrefillVersion = useRef(prefillVersion);

      // ---- Create: seed from the interview plan -----------------------
      // Respect the plan's `scorecard_required` + AI toggle regardless
      // of whether the questions live under `scorecard_config` (flat)
      // or `children[]` (nested). See `planScorecardResolver.ts`.
      useEffect(() => {
        if (prefillVersion > 0) return;
        if (!interviewPlan) return;
        if (didPlanSeed.current) return;

        if (typeof interviewPlan.scorecard_required === 'boolean') {
          setScorecardEnabled(interviewPlan.scorecard_required);
        }
        if (typeof interviewPlan.fill_with_ai_enabled === 'boolean') {
          setAiEnabled(interviewPlan.fill_with_ai_enabled);
        }

        const { raw, plannedName } = resolvePlanScorecardSource(interviewPlan);
        if (!raw) return;
        const qs = Array.isArray(raw.questions) ? raw.questions : [];
        if (qs.length === 0) return;

        didPlanSeed.current = true;
        scorecardSeededFromInterviewPlan.current = true;
        const merged: Record<string, unknown> = {
          ...((payloadRef.current.scorecard ?? {}) as Record<string, unknown>),
          ...raw,
        };
        payloadRef.current.scorecard = merged;
        const values = payloadToFormValues(merged);
        setInitialValues(values);
        // Reset + surface initial errors so a plan that ships incomplete
        // rows (e.g. missing options) reds out instead of staying silent.
        // The `void` discards the trigger promise — we don't await here
        // since this fires during a useEffect tick.
        queueMicrotask(() => {
          formRef.current?.reset(values);
          void formRef.current?.trigger();
        });
        if (templates.length > 0) {
          didAutoSelectTemplate.current = true;
          const pick =
            plannedName && templates.some((t) => t.name === plannedName)
              ? plannedName
              : (templates[0]?.name ?? null);
          setSelectedTemplate(pick);
        }
      }, [interviewPlan, payloadRef, prefillVersion, templates]);

      // ---- Edit: hydrate from payloadRef ------------------------------
      useEffect(() => {
        if (prefillVersion === 0) return;
        const existing = payloadRef.current.scorecard as Record<
          string,
          unknown
        >;
        if (!existing || !('questions' in existing)) return;

        // Header-bound state (toggles + template) isn't covered by the
        // lazy `useState` init for `initialValues`, so always re-sync.
        setScorecardEnabled(
          (existing as { enabled?: boolean }).enabled !== false
        );
        setAiEnabled(
          (existing as { ai_enabled?: boolean }).ai_enabled !== false
        );
        setSelectedTemplate(
          (existing as { template_id?: string }).template_id ?? null
        );

        // Skip the validation pass when the prefill has no questions:
        // `validate()` short-circuits to `true` for an empty scorecard
        // (product allows enabling without questions — overall evaluation
        // is the separate switch). Triggering here would surface the
        // schema's "At least one question is required" red on prefill
        // even though the user can legitimately leave it empty.
        const rawQuestions = Array.isArray(existing.questions)
          ? (existing.questions as unknown[])
          : [];
        const hasQuestions = rawQuestions.length > 0;

        // On the very first mount for this `prefillVersion`, the form
        // already has `existing` baked into `defaultValues` via the lazy
        // init above. Calling `reset(values)` here would replay the same
        // data through `useFieldArray` a second time on the same tick —
        // that's the desync that creates ghost / duplicate rows. Just
        // surface initial errors via `trigger()` and bail.
        if (prefillVersion === initialPrefillVersion.current) {
          if (hasQuestions) void formRef.current?.trigger();
          return;
        }

        // Subsequent prefill bumps (e.g. user re-opens the same drawer
        // with a refreshed payload): the form's existing state is stale,
        // so reset and re-validate atomically.
        const values = payloadToFormValues(existing);
        setInitialValues(values);
        formRef.current?.reset(values);
        if (hasQuestions) void formRef.current?.trigger();
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [prefillVersion]);

      // ---- Adhoc: fall back to first ATS template ---------------------
      // Only when there's no interviewPlan to pull from — plan-driven
      // flows are expected to seed themselves even if they ship empty
      // questions, so we don't want to replace them with a generic
      // first template.
      useEffect(() => {
        if (prefillVersion > 0) return;
        if (interviewPlan) return;
        if (scorecardSeededFromInterviewPlan.current) return;
        if (didAutoSelectTemplate.current) return;
        if (!templates.length || selectedTemplate) return;
        didAutoSelectTemplate.current = true;
        const first = templates[0];
        setSelectedTemplate(first.name);
        const values = templateToFormValues(first);
        setInitialValues(values);
        formRef.current?.reset(values);
      }, [templates, selectedTemplate, prefillVersion, interviewPlan]);

      const handleTemplateChange = useCallback(
        (templateName: string | null) => {
          setSelectedTemplate(templateName);
          const template = templateName
            ? templates.find((t) => t.name === templateName)
            : undefined;
          const values = templateToFormValues(template);
          setInitialValues(values);
          formRef.current?.reset(values);
        },
        [templates]
      );

      useImperativeHandle(
        ref,
        () => ({
          validate: async () => {
            if (!scorecardEnabled) return true;
            if (!formRef.current) return true;
            const formValues = formRef.current.getValues();
            const questionCount = Array.isArray(formValues?.scoreCards)
              ? formValues.scoreCards.length
              : 0;
            // Product behavior: scorecard can be enabled with no
            // custom questions. Overall evaluation is a separate
            // switch inside the builder that stays available.
            if (questionCount === 0) return true;
            return formRef.current.trigger();
          },
          syncToPayload: () => {
            const formValues = formRef.current?.getValues();
            // Preserve round-trip keys from the hydrated payload —
            // specifically `scorecard_child_id` — so edits update the
            // existing backend row instead of appending a new one.
            const prev = (payloadRef.current.scorecard ?? {}) as Record<
              string,
              unknown
            >;
            payloadRef.current.scorecard = {
              ...(typeof prev.scorecard_child_id === 'number'
                ? { scorecard_child_id: prev.scorecard_child_id }
                : {}),
              enabled: scorecardEnabled,
              // Account-level AI gate trumps the local toggle state —
              // never ship `true` when admin has disabled AI globally.
              ai_enabled: showAiToggle && aiEnabled,
              template_id: selectedTemplate,
              is_feedback_mandatory: formValues?.is_feedback_mandatory ?? false,
              questions: (formValues?.scoreCards ?? []).map((q) => ({
                id: q.id,
                question: q.question,
                type: q.type,
                description: q.description,
                mandatory: q.mandatory,
                options: q.options?.map((o) => o.optionText),
              })),
              feedbackDescription: formValues?.feedback_description ?? '',
              ratingDescription: formValues?.rating_description ?? '',
            };
          },
        }),
        [
          aiEnabled,
          payloadRef,
          scorecardEnabled,
          selectedTemplate,
          showAiToggle,
        ]
      );

      return (
        <Flex
          justifyContent="center"
          w="100%"
          minH={0}
          flex="1"
          overflowY="auto"
          key={String(prefillVersion)}
        >
          <Flex w="720px" maxW="100%" flexDir="column" mb="10">
            <ScorecardStepHeader
              scorecardEnabled={scorecardEnabled}
              onScorecardEnabledChange={setScorecardEnabled}
              aiEnabled={aiEnabled}
              onAiEnabledChange={setAiEnabled}
              showAiToggle={showAiToggle}
              templateOptions={templateOptions}
              selectedTemplate={selectedTemplate}
              onTemplateChange={handleTemplateChange}
              copy={copy}
            />

            {scorecardEnabled && (
              <>
                <ScorecardAddQuestion formRef={formRef} />
                <ScorecardTemplateBuilder
                  formRef={formRef}
                  initialValues={initialValues}
                  // Zero questions is a valid scorecard here (overall
                  // evaluation is the separate switch) — matches
                  // `validate()` returning true for an empty list.
                  // Suppresses the builder's "At least one question is
                  // required" empty-state error.
                  minQuestions={0}
                />
              </>
            )}
          </Flex>
        </Flex>
      );
    }
  )
);

ScorecardStep.displayName = 'ScorecardStep';
